import { InformationCircleIcon, LifebuoyIcon } from "@heroicons/react/24/outline";
import Head from "next/head";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useRolePermissionContext, useRolePermissionsLoading } from "@Front/Stores/RolePermissionsStore";
import { useOfficeScopedPermissionCan } from "@Front/Hooks/useOfficeScopedPermissionCan";
import { AppRuleNames } from "@Front/Api/Entities/rule";
import { IconNavigationButton } from "@Front/Components/Layouts/Common/IconNavigationButton";
import { COMMON_UI_I18N } from "@Front/Components/DesignSystem/helpers/commonUiI18n";
import Typography, { ETypo, ETypoColor } from "../Typography";
import BurgerMenu from "./BurgerMenu";
import classes from "./classes.module.scss";
import Navigation from "./Navigation";
import Notifications from "./Notifications";
import Profile from "./Profile";
import LogOut from "../LogOutButton";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import { formatDate } from "@Front/Utils/formatters";
import { isManualSubscription, getActiveSubscription } from "@Front/Utils/subscriptionHelpers";
import { notaryFolderLogoListAriaLabel } from "@Front/Utils/applyFolderAsOperationFr";
import { usePublicSiteBrandingAssets } from "@Front/Services/PublicSiteConfig/usePublicSiteBrandingAssets";
import { usePublicUiOptions } from "@Front/Services/PublicSiteConfig/usePublicUiOptions";
import type { PublicSitePublicUiOptions } from "@Front/Services/PublicSiteConfig/publicSiteConfigLoaderTypes";
import { useStripeSubscriptionSnapshot } from "@Front/Components/Layouts/Subscription/hooks/useStripeSubscriptionSnapshot";
import { useResolvedUserContext } from "@Front/Hooks/useResolvedUserContext";
import { resolveRouteContextFamily } from "@Front/Utils/routeContextRegistry";
import { resolveHeaderLogoHref } from "@Front/Utils/headerLogoNavigation";

function resolveUpcomingCancelAtDate(cancelAtUnix: number | null): Date | null {
	if (typeof cancelAtUnix !== "number") return null;
	const expirationDate = new Date(cancelAtUnix * 1000);
	const daysUntilExpiration = Math.ceil((expirationDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
	if (daysUntilExpiration <= 0 || daysUntilExpiration > 30) return null;
	return expirationDate;
}

async function loadStripeSubscriptionIdForHeader(
	hasOfficeFoldersRead: boolean,
	officeUid: string | null | undefined,
	setStripeSubscriptionId: React.Dispatch<React.SetStateAction<string | null>>,
): Promise<void> {
	if (!officeUid || !hasOfficeFoldersRead) {
		setStripeSubscriptionId(null);
		return;
	}
	try {
		const subscription = await getActiveSubscription(officeUid);
		if (!subscription?.stripe_subscription_id) {
			setStripeSubscriptionId(null);
			return;
		}
		const stripeSubId = subscription.stripe_subscription_id;
		if (isManualSubscription(stripeSubId)) {
			setStripeSubscriptionId(null);
			return;
		}
		setStripeSubscriptionId(stripeSubId);
	} catch (error) {
		setStripeSubscriptionId(null);
		LoggerService.error("[Header] Unable to load subscription banner", { error });
	}
}

function useHeaderStripeSubscriptionId(
	hasOfficeFoldersRead: boolean,
	activeOfficeUid: string | null | undefined,
): string | null {
	const [stripeSubscriptionId, setStripeSubscriptionId] = useState<string | null>(null);
	const loadStripeSubscriptionId = useCallback(
		() => loadStripeSubscriptionIdForHeader(hasOfficeFoldersRead, activeOfficeUid, setStripeSubscriptionId),
		[hasOfficeFoldersRead, activeOfficeUid],
	);
	useEffect(() => {
		void loadStripeSubscriptionId();
	}, [loadStripeSubscriptionId]);
	return stripeSubscriptionId;
}

function useHeaderCancelAtDate(
	hasOfficeFoldersRead: boolean,
	activeOfficeUid: string | null | undefined,
): Date | null {
	const stripeSubscriptionId = useHeaderStripeSubscriptionId(hasOfficeFoldersRead, activeOfficeUid);
	const stripeSnapshot = useStripeSubscriptionSnapshot({
		stripeSubscriptionId,
		enabled: Boolean(stripeSubscriptionId),
		logLabel: "HeaderSubscriptionBanner",
		errorFallbackMessage: "Impossible de charger l'état d'abonnement Stripe.",
	});
	return useMemo(() => resolveUpcomingCancelAtDate(stripeSnapshot.cancelAtUnix), [stripeSnapshot.cancelAtUnix]);
}

type IProps = {
	isUserConnected: boolean;
};

const headerHeight = 75;

function resolveHeaderRenderParams(params: {
	isUserConnected: boolean;
	permissionsLoading: boolean;
	isCustomerOrThirdParty: boolean;
	cancelAt: Date | null;
	publicUi: PublicSitePublicUiOptions;
}): HeaderContentProps {
	return {
		isUserConnected: params.isUserConnected,
		showAuthenticatedContent: params.isUserConnected && !params.permissionsLoading,
		isCustomerOrThirdParty: params.isCustomerOrThirdParty,
		cancelAt: params.cancelAt,
		header_logo_variant: params.publicUi.header_logo_variant,
	};
}

function HeaderRightSection({ isCustomerOrThirdParty }: { isCustomerOrThirdParty: boolean }): React.ReactElement {
	if (isCustomerOrThirdParty) {
		return (
			<div className={classes["right-section"]}>
				<div className={classes["desktop"]}><LogOut variant="button" /></div>
				<div className={classes["mobile"]}><BurgerMenu /></div>
			</div>
		);
	}
	return (
		<div className={classes["right-section"]}>
			<div className={classes["desktop"]}>
				<IconNavigationButton
					href="/incident-report"
					icon={<LifebuoyIcon />}
					ariaLabel={COMMON_UI_I18N.iconButtonAccessibilityLabels.incidentReportNav}
				/>
			</div>
			<Notifications />
			<div className={classes["desktop"]}><Profile /></div>
			<div className={classes["mobile"]}><BurgerMenu /></div>
		</div>
	);
}

type HeaderContentProps = {
	isUserConnected: boolean;
	showAuthenticatedContent: boolean;
	isCustomerOrThirdParty: boolean;
	cancelAt: Date | null;
	header_logo_variant: PublicSitePublicUiOptions["header_logo_variant"];
};

function HeaderLogo(props: {
	isUserConnected: boolean;
	isCustomerOrThirdParty: boolean;
	header_logo_variant: PublicSitePublicUiOptions["header_logo_variant"];
}): React.ReactElement {
	const router = useRouter();
	const { logoMainUrl } = usePublicSiteBrandingAssets();
	const logoClassName = props.header_logo_variant === "compact" ? classes["logoCabinetLine"] : classes["logo"];
	if (props.isUserConnected) {
		const logoHref = resolveHeaderLogoHref(props.isCustomerOrThirdParty);
		const logoAriaLabel = props.isCustomerOrThirdParty
			? "Retour à la liste de vos dossiers"
			: notaryFolderLogoListAriaLabel();
		return (
			<button
				type="button"
				className={classes["logo-nav"]}
				onClick={() => void router.push(logoHref)}
				aria-label={logoAriaLabel}>
				<img src={logoMainUrl} alt="logo" className={logoClassName} />
			</button>
		);
	}
	return <img src={logoMainUrl} alt="logo" className={logoClassName} />;
}

function renderHeaderContent(props: HeaderContentProps): React.ReactElement {
	const { isUserConnected, showAuthenticatedContent, isCustomerOrThirdParty, cancelAt, header_logo_variant } = props;
	return (
		<>
			<div className={classes["root"]}>
				<div className={classes["logo-container"]}>
					<HeaderLogo
						isUserConnected={isUserConnected}
						isCustomerOrThirdParty={isCustomerOrThirdParty}
						header_logo_variant={header_logo_variant}
					/>
				</div>
				{showAuthenticatedContent && !isCustomerOrThirdParty && (
					<>
						<div className={classes["desktop"]}><Navigation /></div>
						<HeaderRightSection isCustomerOrThirdParty={false} />
					</>
				)}
				{showAuthenticatedContent && isCustomerOrThirdParty && (
					<HeaderRightSection isCustomerOrThirdParty />
				)}
			</div>
			{cancelAt && (
				<div className={classes["subscription-line"]}>
					<InformationCircleIcon height="24" />
					<Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.COLOR_GENERIC_BLACK}>
						Assurez vous de sauvegarder tout ce dont vous avez besoin avant la fin de votre abonnement le {formatDate(cancelAt)}.
					</Typography>
				</div>
			)}
		</>
	);
}

export default function Header(props: IProps): React.ReactElement {
	const { isUserConnected } = props;
	const router = useRouter();
	const { pathname } = router;
	const { faviconUrl } = usePublicSiteBrandingAssets();
	const { public_ui } = usePublicUiOptions();
	const permissionContext = useRolePermissionContext();
	const permissionsLoading = useRolePermissionsLoading();
	const { isCustomer: resolvedIsCustomer, isThirdParty: resolvedIsThirdParty } = useResolvedUserContext(permissionContext);
	const officeScopedCan = useOfficeScopedPermissionCan();
	const hasOfficeFoldersRead = officeScopedCan(AppRuleNames.officeFolders, "read");
	const isCustomer = resolvedIsCustomer;
	const isThirdParty = resolvedIsThirdParty;
	const routeContextFamily = resolveRouteContextFamily(pathname);
	const isCustomerOrThirdParty = routeContextFamily === "client" || isCustomer || isThirdParty;
	const cancelAt = useHeaderCancelAtDate(hasOfficeFoldersRead, permissionContext?.activeOfficeUid);
	const headerRenderParams = resolveHeaderRenderParams({
		isUserConnected,
		permissionsLoading,
		isCustomerOrThirdParty,
		cancelAt,
		publicUi: public_ui,
	});

	useEffect(() => {
		document.documentElement.style.setProperty("--header-height", `${headerHeight}px`);
	}, []);

	return (
		<>
			<Head>
				<link rel="shortcut icon" href={faviconUrl} />
				<link rel="icon" href={faviconUrl} />
			</Head>
			{renderHeaderContent(headerRenderParams)}
		</>
	);
}
